"use client";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Logo from "@/components/brand/Logo";
import { savedSearches } from "@/lib/api";
import { userDisplayName } from "@/lib/user-display";
import AdvisorAvatar from "@/components/advisor/AdvisorAvatar";
import { useAuth } from "@/lib/auth-context";
import { useBreakpoint } from "@/lib/hooks";
import { useMaeHeader } from "@/lib/mae-header";
import { SITE_ROUTES, recentSearchLinkForUser } from "@/components/layout/nav-links";

interface RecentSearch {
  label: string;
  href: string;
}

function getInitials(nameOrEmail: string) {
  const text = nameOrEmail.trim();
  if (!text) return "U";
  const parts = text.includes("@") ? [text[0]] : text.split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map(part => part[0]).join("").toUpperCase();
}

export default function Nav() {
  const { user, logout } = useAuth();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [recentSearch, setRecentSearch] = useState<RecentSearch | null>(null);
  const [menuCounts, setMenuCounts] = useState({ activeWatches: 0, newMatches: 0, watchedHomes: 0, hiddenHomes: 0, unreadAgentMessages: 0 });
  const { isMobile, isNarrow } = useBreakpoint();
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const maeHeader = useMaeHeader();

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && accountMenuRef.current && !accountMenuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  // Close the account menu on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    setRecentSearch(recentSearchLinkForUser(user));
  }, [pathname, user]);

  useEffect(() => {
    if (!user) {
      setMenuCounts({ activeWatches: 0, newMatches: 0, watchedHomes: 0, hiddenHomes: 0, unreadAgentMessages: 0 });
      return;
    }

    let cancelled = false;
    const localActions = (() => {
      try {
        const parsed = JSON.parse(window.localStorage.getItem("openhomesai_listing_actions") || "{}");
        return {
          watchedHomes: Array.isArray(parsed.watched) ? parsed.watched.length : 0,
          hiddenHomes: Array.isArray(parsed.hidden) ? parsed.hidden.length : 0,
        };
      } catch {
        return { watchedHomes: 0, hiddenHomes: 0 };
      }
    })();
    const unreadAgentMessages = (() => {
      try {
        const raw = window.localStorage.getItem("openhomesai_unread_agent_messages");
        const count = raw ? Number(raw) : 0;
        return Number.isFinite(count) ? count : 0;
      } catch {
        return 0;
      }
    })();

    savedSearches.list()
      .then(response => {
        if (cancelled) return;
        const searches = Array.isArray(response.data) ? response.data : [];
        setMenuCounts({
          activeWatches: searches.filter((search: { alert_enabled?: boolean }) => search.alert_enabled !== false).length,
          newMatches: searches.reduce((sum: number, search: { new_matches_count?: number | null }) => sum + (search.new_matches_count || 0), 0),
          watchedHomes: localActions.watchedHomes,
          hiddenHomes: localActions.hiddenHomes,
          unreadAgentMessages,
        });
      })
      .catch(() => {
        if (!cancelled) setMenuCounts({ activeWatches: 0, newMatches: 0, ...localActions, unreadAgentMessages });
      });

    return () => {
      cancelled = true;
    };
  }, [pathname, user]);

  if (pathname.startsWith("/onboarding")) return null;

  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);
  // On a phone, Mae's page gets Mae's header in this bar rather than a second
  // one stacked beneath it: a menu button opens the page's drawer, which holds
  // the logo, her sections and the site links; her title and New chat follow.
  // The account initials leave the bar here, since Profile is in the drawer.
  // Desktop is untouched. Signed-out visitors see the ordinary header.
  const showMaeHeader = isMobile && Boolean(user) && isActive(SITE_ROUTES.mae);
  // A 44px tap target for the smaller phone logo, by padding rather than size.
  const brandStyle: CSSProperties = isMobile
    ? { marginRight: "auto", minHeight: 44, padding: "0 4px", marginLeft: -4 }
    : { marginRight: 34 };
  // The Mae header's menu icon (phones only; the header never renders on desktop):
  // three bars 22px wide, stroke and gap scaled from the old 18px icon's 2px and 4px
  // so it reads the same, only larger. The unread dot sits centred on the icon's
  // top-right corner, placed from these numbers so the two stay aligned.
  const MENU_BUTTON = 44;
  const MENU_ICON_WIDTH = 22;
  const MENU_BAR = 2.5;
  const MENU_BAR_GAP = 5;
  const MENU_ICON_HEIGHT = MENU_BAR * 3 + MENU_BAR_GAP * 2;
  const MENU_DOT = 10;
  const MENU_DOT_BORDER = 2.5;
  const menuDotOuter = MENU_DOT + MENU_DOT_BORDER * 2;
  const menuBarStyle: CSSProperties = { display: "block", height: MENU_BAR, borderRadius: "var(--radius-sm)", background: "currentColor" };
  const navLinkStyle: CSSProperties = {
    color: "var(--color-nav-link)",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    borderRadius: "var(--radius-md)",
    padding: "8px 10px",
    textDecoration: "none",
  };
  const badge = (value: number | string) => <span className="app-account-menu-badge">{value}</span>;
  // "Mae" with a small superscript "AI" so it reads as the AI agent, not a name fragment.
  // position:relative keeps it out of the line box, so other nav items stay baseline-aligned.
  const mayNavLabel = (
    <>Mae<span style={{ fontSize: 12, fontWeight: 700, position: "relative", top: "-0.35em", marginLeft: "2px", letterSpacing: "0.02em" }}>AI</span></>
  );

  return (
    <>
      {/* The nav is position: fixed (iOS WebKit does not reliably honor
          position: sticky under a root/body overflow-x — even clip — so sticky
          silently failed on phones). This spacer keeps the nav's 58px layout
          slot, so every calc that subtracts var(--nav-height) — the chat
          column's height, the shell's min-height — stays correct. It renders
          only when the nav does: /onboarding returns null above and gets
          neither. */}
      <div aria-hidden style={{ height: "var(--nav-height)", flexShrink: 0 }} />
      <nav
      className="app-nav"
      style={{
        padding: isMobile ? "0 14px" : "0 24px",
      }}
    >
      {showMaeHeader ? (
        <button
          type="button"
          onClick={maeHeader.onOpenMenu ?? undefined}
          aria-label="Open menu"
          aria-haspopup="dialog"
          style={{ position: "relative", width: 44, height: 44, marginLeft: -8, marginRight: 4, flexShrink: 0, border: "none", background: "transparent", color: "var(--color-ink)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, fontFamily: "inherit" }}
        >
          <span aria-hidden="true" style={{ display: "grid", gap: MENU_BAR_GAP, width: MENU_ICON_WIDTH }}>
            <span style={menuBarStyle} />
            <span style={menuBarStyle} />
            <span style={menuBarStyle} />
          </span>
          {maeHeader.menuSignal && (
            // Something in the drawer needs the buyer: unread updates, or a
            // question Mae is waiting on. Never decorative.
            <span aria-hidden="true" style={{ position: "absolute", top: (MENU_BUTTON - MENU_ICON_HEIGHT - menuDotOuter) / 2, right: (MENU_BUTTON - MENU_ICON_WIDTH - menuDotOuter) / 2, width: MENU_DOT, height: MENU_DOT, borderRadius: "var(--radius-full)", background: "var(--color-brand)", border: `${MENU_DOT_BORDER}px solid var(--color-surface)` }} />
          )}
        </button>
      ) : (
        <Link
          href={SITE_ROUTES.home}
          className="app-nav-brand"
          aria-label="OpenHomesAI home"
          style={brandStyle}
        >
            <Logo size={isMobile ? 20 : 24} markSize={isMobile ? 20 : undefined} textSize={isMobile ? 12 : 16} />
        </Link>
      )}

      {showMaeHeader && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 auto", minWidth: 0 }}>
          <AdvisorAvatar size="xs" />
          <div style={{ minWidth: 0, display: "grid" }}>
            <span style={{ color: "var(--color-ink)", fontSize: 16, lineHeight: 1.25, fontWeight: 600 }}>Mae</span>
            {maeHeader.checkLabel && (
              <span style={{ color: maeHeader.checkWarning ? "var(--color-warning-text)" : "var(--color-muted)", fontSize: 12, lineHeight: 1.25, fontWeight: maeHeader.checkWarning ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {maeHeader.checkLabel}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="app-nav-links" style={{ display: isMobile ? "none" : undefined }}>
        <Link href="/" className={`app-nav-link ${isActive("/") ? "active" : ""}`} style={navLinkStyle}>Search</Link>
        {user && <Link href="/assistant-profile" className={`app-nav-link ${isActive("/assistant-profile") ? "active" : ""}`} style={{ ...navLinkStyle, display: "inline-flex", alignItems: "center", gap: 6 }}><AdvisorAvatar size="xs" />{mayNavLabel}</Link>}
        {user && <Link href="/searches" className={`app-nav-link ${isActive("/searches") || isActive("/favorites") ? "active" : ""}`} style={navLinkStyle}>Saved</Link>}
        <Link href="/about" className={`app-nav-link ${isActive("/about") ? "active" : ""}`} style={navLinkStyle}>About</Link>
      </div>

      <div className="app-nav-actions" style={{ marginLeft: isMobile ? 8 : 18 }}>
        {showMaeHeader && maeHeader.onNewChat && (
          <button
            type="button"
            onClick={maeHeader.onNewChat}
            style={{
              minHeight: 44,
              display: "inline-flex",
              alignItems: "center",
              border: "1px solid var(--color-border-2)",
              borderRadius: "var(--radius-md)",
              background: "var(--color-surface)",
              color: "var(--color-ink)",
              padding: "0 12px",
              fontSize: 14,
              lineHeight: 1.4,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            New chat
          </button>
        )}
        {/* Below 480px the text links cannot share the bar with the account trigger
            without pushing it off-screen (measured: the avatar sat at x 386-430 on a
            375 and a 390 wide phone). There the signed-in links live in the account
            menu instead, and a signed-out visitor keeps Log in and Sign up. */}
        {isMobile && !isNarrow && !showMaeHeader && (
          <>
            <Link href="/" className={`app-nav-link ${isActive("/") ? "active" : ""}`} style={{ ...navLinkStyle, fontSize: 14, padding: "5px 7px" }}>
              Search
            </Link>
            {user && (
              <Link href="/assistant-profile" className={`app-nav-link ${isActive("/assistant-profile") ? "active" : ""}`} style={{ ...navLinkStyle, fontSize: 14, padding: "5px 7px", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <AdvisorAvatar size="xs" />
                {mayNavLabel}
              </Link>
            )}
            {user && (
              <Link href="/searches" className={`app-nav-link ${isActive("/searches") || isActive("/favorites") ? "active" : ""}`} style={{ ...navLinkStyle, fontSize: 14, padding: "5px 7px" }}>
                Saved
              </Link>
            )}
            <Link href="/about" className={`app-nav-link ${isActive("/about") ? "active" : ""}`} style={{ ...navLinkStyle, fontSize: 14, padding: "5px 7px" }}>
              About
            </Link>
          </>
        )}
        {user && !showMaeHeader ? (
          <div
            className="app-account-menu-wrap"
            ref={accountMenuRef}
          >
            <button
              className={`app-account-trigger ${menuOpen ? "active" : ""}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(open => !open)}
              style={isMobile ? { minWidth: 44, minHeight: 44, justifyContent: "center" } : undefined}
            >
              <span className="app-account-avatar">{getInitials(user.name || user.email)}</span>
              <span className="app-account-copy">
                <span className="app-account-greeting">Hi, {(user.name || user.email).split(" ")[0]}</span>
              </span>
            </button>

            {menuOpen && (
              <div className="app-account-menu" role="menu">
                <div className="app-account-menu-header">
                  <span className="app-account-avatar large">{getInitials(user.name || user.email)}</span>
                  <div>
                    <p>{userDisplayName(user)}</p>
                    <span>{user.email}</span>
                  </div>
                </div>

                {isNarrow && (
                  <div className="app-account-menu-section">
                    <p className="app-account-menu-label">Site</p>
                    <Link href="/" className="app-account-menu-item" role="menuitem">
                      <span>Search</span>
                      <em>›</em>
                    </Link>
                    <Link href="/assistant-profile" className="app-account-menu-item" role="menuitem">
                      <span>{mayNavLabel}</span>
                      <em>›</em>
                    </Link>
                    <Link href="/searches" className="app-account-menu-item" role="menuitem">
                      <span>Saved</span>
                      <em>›</em>
                    </Link>
                    <Link href="/about" className="app-account-menu-item" role="menuitem">
                      <span>About</span>
                      <em>›</em>
                    </Link>
                  </div>
                )}
                <div className="app-account-menu-section">
                  <p className="app-account-menu-label">My search</p>
                  <Link href={SITE_ROUTES.profile} className="app-account-menu-item primary" role="menuitem">
                    <span>
                      <strong>Dashboard</strong>
                      <small>Your AI home search command center</small>
                    </span>
                    {menuCounts.newMatches > 0 ? badge(`${menuCounts.newMatches} new`) : <em>›</em>}
                  </Link>
                  <Link href={SITE_ROUTES.savedSearches} className="app-account-menu-item" role="menuitem">
                    <span>Saved</span>
                    {menuCounts.activeWatches > 0 ? badge(`${menuCounts.activeWatches} watch${menuCounts.activeWatches === 1 ? "" : "es"}`) : <em>›</em>}
                  </Link>
                  <Link href="/profile#watched-homes" className="app-account-menu-item" role="menuitem">
                    <span>Watched homes</span>
                    {menuCounts.watchedHomes > 0 ? badge(menuCounts.watchedHomes) : <em>›</em>}
                  </Link>
                </div>

                <div className="app-account-menu-section">
                  <p className="app-account-menu-label">My activity</p>
                  {recentSearch ? (
                    <Link href={recentSearch.href} className="app-account-menu-item primary" role="menuitem">
                      <span>
                        <strong>Recently viewed</strong>
                        <small>{recentSearch.label}</small>
                      </span>
                      <em>›</em>
                    </Link>
                  ) : (
                    <Link href={SITE_ROUTES.home} className="app-account-menu-item primary" role="menuitem">
                      <span>
                        <strong>Start a search</strong>
                        <small>Try a town, budget, or must-have</small>
                      </span>
                      <em>›</em>
                    </Link>
                  )}
                  <Link href="/profile#hidden-homes" className="app-account-menu-item" role="menuitem">
                    <span>Hidden homes</span>
                    {menuCounts.hiddenHomes > 0 ? badge(menuCounts.hiddenHomes) : <em>›</em>}
                  </Link>
                  <Link href="/profile#tours" className="app-account-menu-item" role="menuitem">
                    <span>Tours / agent messages</span>
                    {menuCounts.unreadAgentMessages > 0 ? badge(`${menuCounts.unreadAgentMessages} unread`) : <em>›</em>}
                  </Link>
                  <Link href="/assistant-profile" className="app-account-menu-item" role="menuitem">
                    <span>Mae's memory</span>
                    <em>›</em>
                  </Link>
                </div>

                <div className="app-account-menu-section">
                  <p className="app-account-menu-label">Settings</p>
                  <Link href="/profile#notifications" className="app-account-menu-item" role="menuitem">
                    <span>Notification settings</span>
                    <em>›</em>
                  </Link>
                  <Link href="/profile#account-settings" className="app-account-menu-item" role="menuitem">
                    <span>Account settings</span>
                    <em>›</em>
                  </Link>
                  <Link href="/auth/forgot-password" className="app-account-menu-item" role="menuitem">
                    <span>Password and sign-in</span>
                    <em>›</em>
                  </Link>
                  <button onClick={logout} className="app-account-menu-item danger" role="menuitem">
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : user ? null : (
          <>
            <Link href="/auth/login" className="app-nav-link" style={navLinkStyle}>Log in</Link>
            <Link href="/auth/signup" className="app-nav-primary">
              Sign up
            </Link>
          </>
        )}
      </div>
    </nav>
    </>
  );
}
